import { Component, OnInit } from '@angular/core';
import {LoadingController, ModalController, NavParams, ToastController,} from '@ionic/angular';
import {Evaluation360Record} from 'src/app/360-evaluation/_interfaces/360-evaluation-record.interface';
import {FreeEvaluationReport} from 'src/app/final-report/_interfaces/final-report.interface';
import {BaseClass} from 'src/app/shared/classes/base';
import {ProcessConfig, QuestionTypeEnum} from 'src/app/shared/interfaces/process-config.interface';
import {TranslationService} from 'src/app/shared/services/translation.service';
import * as _ from 'lodash';
import {AreaConfig} from 'src/app/360-evaluation/_interfaces/area-config.interface';
import {ManagerEvaluationService} from '../../../manager-evaluation/manager-evaluation.service';
import { ConfigService } from 'src/app/shared/services/multi-clients/config.service';

@Component({
    selector: 'app-review-evaluations-modal',
    templateUrl: './review-evaluations.modal.html',
    styleUrls: ['./review-evaluations.modal.scss'],
})
export class ReviewEvaluationsModal extends BaseClass implements OnInit {
    public evaluations: Array<Evaluation360Record> = [];
    public freeEvaluationsMidYear: Array<FreeEvaluationReport>;
    public freeEvaluationsEndYear: Array<FreeEvaluationReport>;
    public externalThreeSixtyEvaluations: Array<Evaluation360Record> = [];
    public externalFreeEvaluationsMidYear: Array<FreeEvaluationReport>;
    public externalFreeEvaluationsEndYear: Array<FreeEvaluationReport>;
    public managerEvaluation: Array<Evaluation360Record> = [];
    public areas: AreaConfig[];
    public evaluationByArea: { [key: string]: Array<Evaluation360Record> } = {};
    public processConfig: ProcessConfig;
    public freeEvaluationMidYearConfig: ProcessConfig;
    public freeEvaluationEndYearConfig: ProcessConfig;

    public hideExternalEvalFromManagerCard: boolean;
    public divide360UsersLitByAreaEnabled: boolean;
    public getManagerEvaluation: boolean = false;

    private _cycleId: number;
    private _userId: string;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _modalController: ModalController,
        private _loadingController: LoadingController,
        private _navParams: NavParams,
        private _evaluationService: ManagerEvaluationService,
        private _configService: ConfigService
    ) {
        super(_toastController, _translationService);
        this._cycleId = this._navParams.get('cycleId');
        this._userId = this._navParams.get('userId');
        this.getManagerEvaluation = this._navParams.get('getManagerEvaluation');
    }

    async ngOnInit() {
        this.hideExternalEvalFromManagerCard = this._configService.settings.features.hideExternalEvalFromManagerCard;
        this.divide360UsersLitByAreaEnabled = this._configService.settings.features.divide360UsersLitByArea;
        await this._loadEvaluationResults();
    }

    public async dismiss() {
        await this._modalController.dismiss();
    }

    private async _loadEvaluationResults(): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        setTimeout(() => {
            this._evaluationService
                .get360EvaluationResults(this._cycleId, this._userId, this.getManagerEvaluation)
                .subscribe(
                    async (response) => {
                        this.processConfig = response.data.config;
                        if (this.divide360UsersLitByAreaEnabled) {
                            this.areas = this.processConfig.areas;
                            this.evaluationByArea = _.groupBy(response.data.evaluations, 'areaId');
                        }
                        this.freeEvaluationMidYearConfig =
                            response.data.freeEvaluationMidYearConfig;
                        this.freeEvaluationEndYearConfig =
                            response.data.freeEvaluationEndYearConfig;

                        this.evaluations = response.data.evaluations;
                        this.freeEvaluationsMidYear = response.data.freeEvaluationsMidYear;
                        this.freeEvaluationsEndYear = response.data.freeEvaluationsEndYear;
                        this.externalThreeSixtyEvaluations =
                            response.data.externalThreeSixtyEvaluations;
                        this.externalFreeEvaluationsMidYear =
                            response.data.externalFreeEvaluationsMidYear;
                        this.externalFreeEvaluationsEndYear =
                            response.data.externalFreeEvaluationsEndYear;
                        this.managerEvaluation = [...this.managerEvaluation, response.data.managerEvaluation];

                        await this.removeCalculateQuestions();
                        await loading.dismiss();
                    },
                    (error) => {
                        this.notifyError(error);
                        loading.dismiss();
                    }
                );
        });
    }

    private async removeCalculateQuestions() {
        if (!this.processConfig) return;

        let calculateQuestions: number[];

        if (this.processConfig.questions) {
            calculateQuestions = _.chain(this.processConfig.questions)
                .filter(qc => qc.typeConfig.type === QuestionTypeEnum.Calculate)
                .map(qc => qc.order)
                .value();
            _.remove(this.processConfig.questions, qc => qc.typeConfig.type === QuestionTypeEnum.Calculate);
        } else if (this.processConfig.questionsGroups) {
            calculateQuestions = _.chain(this.processConfig.questionsGroups)
                .flatMap(qg => qg.questions)
                .filter(qc => qc.typeConfig.type === QuestionTypeEnum.Calculate)
                .map(qc => qc.order)
                .value();
            this.processConfig.questionsGroups.forEach(qg => _.remove(qg.questions, qc => qc.typeConfig.type === QuestionTypeEnum.Calculate));
        }

        if (this.evaluations) await this.removeFieldsFromEvaluations(this.evaluations, calculateQuestions);
        if (this.freeEvaluationsMidYear) await this.removeFieldsFromEvaluations(this.freeEvaluationsMidYear, calculateQuestions);
        if (this.freeEvaluationsEndYear) await this.removeFieldsFromEvaluations(this.freeEvaluationsEndYear, calculateQuestions);
        if (this.externalThreeSixtyEvaluations) await this.removeFieldsFromEvaluations(this.externalThreeSixtyEvaluations, calculateQuestions);
        if (this.externalFreeEvaluationsMidYear) await this.removeFieldsFromEvaluations(this.externalFreeEvaluationsMidYear, calculateQuestions);
        if (this.externalFreeEvaluationsEndYear) await this.removeFieldsFromEvaluations(this.externalFreeEvaluationsEndYear, calculateQuestions);
    }

    private async removeFieldsFromEvaluations(evaluationArray: Array<any>, calculateQuestions: number[]) {
        if (evaluationArray.length > 0) {
            evaluationArray.forEach(evaluation => {
                calculateQuestions.forEach(question => {
                    const fieldName = `field${question}`;
                    if (fieldName in evaluation) {
                        delete evaluation[fieldName];
                    }
                });
            });
        }
    }
}
